import { memo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { CellValue, IdType } from 'react-table'
import {
  mdiArrowRight,
  mdiCircle,
  mdiCircleOutline,
  mdiPowerPlug,
} from '@mdi/js'
import type { State } from '@racepointenergy/swc/scs/features/host-states'

import { messages } from '../messages'
import theme from 'theme_'
import { Body12, BodyPrimary } from 'components/Typography/Labels'
import {
  MobileTableBlock,
  MobileTableBlockCell,
  MobileTableBlockRow,
} from 'components/Table/MobileBlock/styles'
import { ChannelMobileWrapper, StyledMForwardIcon } from '../styles'
import Icon from '@mdi/react'

interface PanelBlockProps {
  values: Record<IdType<State>, CellValue>
  original: Record<IdType<State>, CellValue>
}

const channel = (status: number, circuit: string, hwUID: string) => {
  switch (status) {
    case 0:
      return (
        <>
          <MobileTableBlockCell>
            <ChannelMobileWrapper>
              <Icon
                path={mdiCircleOutline}
                size={0.75}
                color={theme.colors.horizonOrange}
              />
              <Body12 color={theme.colors.darkeyGrey}>
                <FormattedMessage {...messages.headerChannel} />{' '}
                {hwUID ? (parseInt(hwUID, 16) % 2 === 0 ? 'B' : 'A') : '-'}
              </Body12>
            </ChannelMobileWrapper>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <BodyPrimary>{circuit}</BodyPrimary>
          </MobileTableBlockCell>
        </>
      )
    case 1:
      return (
        <>
          <MobileTableBlockCell>
            <ChannelMobileWrapper>
              <Icon
                path={mdiCircle}
                size={0.75}
                color={theme.colors.successGreen}
              />
              <Body12 color={theme.colors.darkeyGrey}>
                <FormattedMessage {...messages.headerChannel} />{' '}
                {hwUID ? (parseInt(hwUID, 16) % 2 === 0 ? 'B' : 'A') : '-'}
              </Body12>
            </ChannelMobileWrapper>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <BodyPrimary>{circuit}</BodyPrimary>
          </MobileTableBlockCell>
        </>
      )
    case 2:
      return (
        <>
          <MobileTableBlockCell>
            <ChannelMobileWrapper>
              <Icon
                path={mdiPowerPlug}
                size={0.75}
                color={theme.colors.otterWarmBlack}
              />
              <Body12 color={theme.colors.darkeyGrey}>
                <FormattedMessage {...messages.headerChannel} />{' '}
                {hwUID ? (parseInt(hwUID, 16) % 2 === 0 ? 'B' : 'A') : '-'}
              </Body12>
            </ChannelMobileWrapper>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <BodyPrimary>{circuit}</BodyPrimary>
          </MobileTableBlockCell>
        </>
      )
    default:
      return (
        <>
          <MobileTableBlockCell>
            <Body12 color={theme.colors.darkeyGrey}>
              <FormattedMessage {...messages.headerCircuitName} />
            </Body12>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <BodyPrimary>{circuit}</BodyPrimary>
          </MobileTableBlockCell>
        </>
      )
  }
}

export function PanelBlock({ values, original }: PanelBlockProps) {
  const { formatMessage } = useIntl()
  if (!values.Circuit) return null
  return (
    <MobileTableBlock>
      <MobileTableBlockRow>
        <MobileTableBlockCell>
          <BodyPrimary bold color={theme.colors.otterWarmBlack}>
            {values.Type}
          </BodyPrimary>
        </MobileTableBlockCell>
        <MobileTableBlockCell>
          {values.Circuit && (
            <StyledMForwardIcon path={mdiArrowRight} size={1} />
          )}
        </MobileTableBlockCell>
      </MobileTableBlockRow>
      <MobileTableBlockRow>
        <MobileTableBlockCell>
          <Body12 color={theme.colors.darkeyGrey}>
            {formatMessage(messages.headerSlotNumber)}
          </Body12>
        </MobileTableBlockCell>
        <MobileTableBlockCell>
          <BodyPrimary color={theme.colors.otterWarmBlack}>
            {values.Slot}
          </BodyPrimary>
        </MobileTableBlockCell>
      </MobileTableBlockRow>
      <MobileTableBlockRow>
        {channel(values.Channel, values.Circuit, original.hwUID)}
      </MobileTableBlockRow>
    </MobileTableBlock>
  )
}

export default memo(PanelBlock)
